import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GridLogicOperator, type GridFilterModel, type GridSortModel } from '@mui/x-data-grid-pro';

const SORT_PARAM = 'sort';
const FILTERS_PARAM = 'filters';

function parseSortParameter(value: string | null): GridSortModel {
  if (!value) return [];
  const [field, sort] = value.split(':');
  if (!field || !sort) return [];
  if (sort !== 'asc' && sort !== 'desc') return [];
  return [{ field, sort }];
}

function emptyFilterModel(): GridFilterModel {
  return { items: [] };
}

function parseFiltersParameter(value: string | null): GridFilterModel {
  if (!value) return emptyFilterModel();
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return { items: parsed };
    if (parsed && Array.isArray(parsed.items)) {
      const model: GridFilterModel = { items: parsed.items };
      if (parsed.logicOperator === 'or') model.logicOperator = GridLogicOperator.Or;
      return model;
    }
    return emptyFilterModel();
  } catch {
    return emptyFilterModel();
  }
}

export function useGridStateParameters() {
  const [searchParameters, setSearchParameters] = useSearchParams();

  const sortModel = useMemo(() => parseSortParameter(searchParameters.get(SORT_PARAM)), [searchParameters]);

  const filterModel = useMemo(() => parseFiltersParameter(searchParameters.get(FILTERS_PARAM)), [searchParameters]);

  const onSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      setSearchParameters(previous => {
        const next = new URLSearchParams(previous);
        if (newModel.length > 0 && newModel[0].sort) {
          next.set(SORT_PARAM, `${newModel[0].field}:${newModel[0].sort}`);
        } else {
          next.delete(SORT_PARAM);
        }
        return next;
      });
    },
    [setSearchParameters]
  );

  const onFilterModelChange = useCallback(
    (newModel: GridFilterModel) => {
      setSearchParameters(previous => {
        const next = new URLSearchParams(previous);

        if (newModel.items.length > 0) {
          const serialized: { items: typeof newModel.items; logicOperator?: string } = { items: newModel.items };
          if (newModel.logicOperator === GridLogicOperator.Or) serialized.logicOperator = 'or';
          next.set(FILTERS_PARAM, JSON.stringify(serialized));
        } else {
          next.delete(FILTERS_PARAM);
        }
        return next;
      });
    },
    [setSearchParameters]
  );

  const onNegationChange = useCallback(
    (itemId: string | number, negated: boolean, currentFilterModel: GridFilterModel) => {
      const updatedItems = currentFilterModel.items.map(item =>
        item.id === itemId ? { ...item, negated: negated || undefined } : item
      );
      setSearchParameters(previous => {
        const next = new URLSearchParams(previous);
        const serialized: { items: typeof updatedItems; logicOperator?: string } = { items: updatedItems };
        if (currentFilterModel.logicOperator === GridLogicOperator.Or) serialized.logicOperator = 'or';
        next.set(FILTERS_PARAM, JSON.stringify(serialized));
        return next;
      });
    },
    [setSearchParameters]
  );

  return { sortModel, filterModel, onSortModelChange, onFilterModelChange, onNegationChange };
}
